// @ts-nocheck
import { Box, Card, CardContent } from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { GridColumns } from '@mui/x-data-grid'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { IScheduledTask, ITaskResource } from 'src/generated-types/scheduledTask'
import { z } from 'zod'
import { useGetLocalitiesNameAndLogo } from '../../api/localities'
import { useGetAllResources } from '../../api/task-resource'
import { useGetFilterCalendarScheduleTask } from '../../api/tasks'
import { AppRoutesNavigation } from '../../app-routes'
import { useNavigation } from '../../context/NavigationContext'
import { ILocality } from '../../generated-types/locality'
import { ITask } from '../../generated-types/task'
import { useZodForm } from '../../hooks/zod-form'
import TaskCalendar from '../../screens/customer/tasks/tasks/TaskCalendar'
import { doDateRangesOverlap, getFormattedDate, getMonthDateRange, coreUtils, getThemeColor } from '../../utils'
import { DynamicTextTypography } from '../DynamicTextTypography'
import VesselSidebar from '../sidemenu/VesselSidebar'
import GenericTable from '../table/GenericTable'
import { MUIThemeColors } from 'src/mui-theme'

interface VesselTasksCalendarDialogProps {
  customerId: number
  vesselId?: number
  pop?: (data?: any) => void
  paramsStartDate?: Date | string
  paramsEndDate?: Date | string
}

export interface TaskCalendarEvent {
  id: number
  title: string
  startDate: Date | string
  endDate: Date | string
  locality?: ILocality
  localityName?: string
  task: ITask
  vesselName?: string
  rowId?: number
}

interface QueryParams {
  resourceIds?: string
  localityIds?: string
  startDate?: Date
  endDate?: Date
}

const VesselTasksCalendarDialog: React.FC<VesselTasksCalendarDialogProps> = ({
  customerId,
  vesselId,
  pop,
  paramsEndDate,
  paramsStartDate,
}) => {
  const { t } = useTranslation(['common', 'task'])
  const { navigateTo } = useNavigation()
  const initialDateRange = useMemo(() => getMonthDateRange(new Date()), [])
  const [selectedTasks, setSelectedTasks] = useState<TaskCalendarEvent[]>([])
  const theme = useTheme()

  const [queryParams, setQueryParams] = useState<QueryParams>(
    vesselId
      ? {
          resourceIds: String(vesselId),
          startDate: initialDateRange.startDate,
          endDate: initialDateRange.endDate,
        }
      : {
          startDate: initialDateRange.startDate,
          endDate: initialDateRange.endDate,
        },
  )

  const [defaultFilterValues, setDefaultFilterValues] = useState({
    startDate: initialDateRange.startDate,
    endDate: initialDateRange.endDate,
    resourceIds: vesselId ? [{ id: vesselId }] : [],
  })
  const { data: localities } = useGetLocalitiesNameAndLogo(customerId)
  const { data: resources, isLoading: isResourcesLoading } = useGetAllResources(customerId, undefined, undefined, true)
  const { data: tasksData, isLoading } = useGetFilterCalendarScheduleTask(customerId, queryParams)

  useEffect(() => {
    if (vesselId) {
      setQueryParams(prev => ({
        ...prev,
        resourceIds: String(vesselId),
      }))
    }
  }, [vesselId])

  const filterSchema = z
    .object({
      resourceIds: z.array(z.object({ id: z.number() })).nullable(),
      localityIds: z.array(z.object({ id: z.number() })).nullable(),
      startDate: z.date().optional().nullable(),
      endDate: z.date().optional().nullable(),
    })
    .refine(
      data =>
        data.endDate == undefined ||
        data.startDate == undefined ||
        (data.endDate != undefined && data.startDate != undefined && data.endDate > data.startDate),
      {
        message: t('endStartDateError', { ns: 'common' }),
        path: ['endDate'],
      },
    )

  const form = useZodForm(filterSchema, {
    defaultValues: {
      resourceIds: [],
      localityIds: [],
      startDate: defaultFilterValues.startDate,
      endDate: defaultFilterValues.endDate,
    },
  })

  useEffect(() => {
    // Only reset on initial mount or when vesselId changes, preserve current selections
    const currentResourceIds = form.getValues('resourceIds') || []
    const currentLocalityIds = form.getValues('localityIds') || []
    form.reset({
      resourceIds: defaultFilterValues.resourceIds || currentResourceIds,
      localityIds: currentLocalityIds,
      startDate: defaultFilterValues.startDate,
      endDate: defaultFilterValues.endDate,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vesselId])

  const dateRangeChange = useCallback(
    (date: Date) => {
      const { startDate: monthStartDate, endDate: monthEndDate } = getMonthDateRange(date)

      // Preserve current resourceIds and localityIds when updating date range
      const currentResourceIds = (vesselId ? [{ id: vesselId }] : form.getValues('resourceIds')) || []
      const currentLocalityIds = form.getValues('localityIds') || []

      setDefaultFilterValues(prev => ({
        ...prev,
        startDate: monthStartDate,
        endDate: monthEndDate,
        resourceIds: prev.resourceIds || currentResourceIds,
      }))

      // Update query params while preserving current filters
      setQueryParams(prevParams => {
        const newParams: QueryParams = {
          ...prevParams,
          startDate: monthStartDate,
          endDate: monthEndDate,
        }

        if (currentResourceIds.length > 0) {
          newParams.resourceIds = currentResourceIds.map(r => r.id).join(',')
        }

        if (currentLocalityIds.length > 0) {
          newParams.localityIds = currentLocalityIds.map(l => l.id).join(',')
        }

        return newParams
      })
      setSelectedTasks([])
    },
    [form, vesselId],
  )

  const taskFilterChange = useCallback(
    (params: { resourceIds?: Array<{ id: number }>; localityIds?: Array<{ id: number }> }) => {
      const newParams: QueryParams = {
        startDate: defaultFilterValues.startDate,
        endDate: defaultFilterValues.endDate,
      }

      if (params.resourceIds && params.resourceIds.length > 0) {
        newParams.resourceIds = params.resourceIds.map(r => r.id).join(',')
      }

      if (params.localityIds && params.localityIds.length > 0) {
        newParams.localityIds = params.localityIds.map(l => l.id).join(',')
      }

      setQueryParams(newParams)
    },
    [defaultFilterValues],
  )

  const selectedResourceIds = form.watch('resourceIds') || []

  const events = useMemo((): TaskCalendarEvent[] => {
    if (!tasksData) return []
    const selectedResourceIdArray = selectedResourceIds.map(r => r.id)
    const tasks = [] as TaskCalendarEvent[]

    tasksData.forEach((scheduledTask: IScheduledTask) => {
      let eventStartDate = scheduledTask.startDate
      let eventEndDate = scheduledTask.endDate

      // If a vessel is selected, use the resource's assigned period instead of scheduled task dates
      if (selectedResourceIdArray.length > 0 && scheduledTask.taskResources) {
        const matchingResources = (scheduledTask.taskResources as ITaskResource[]).filter(
          tr => tr.resourceId && selectedResourceIdArray.includes(tr.resourceId),
        )
        if (matchingResources.length > 0) {
          matchingResources.forEach((matchingResource, index) => {
            if (matchingResource.startDate) {
              eventStartDate = matchingResource.startDate
            }
            if (matchingResource.endDate) {
              eventEndDate = matchingResource.endDate
            }
            tasks.push({
              rowId: index,
              id: scheduledTask.id,
              title: scheduledTask.tasks.title,
              startDate: eventStartDate,
              endDate: eventEndDate,
              locality: scheduledTask.tasks.locality,
              localityName: scheduledTask.tasks.locality?.name,
              task: scheduledTask.tasks,
              vesselName: matchingResource.resourceName,
            })
          })
        }
      }

      if (selectedResourceIdArray.length == 0)
        tasks.push({
          id: scheduledTask.id,
          title: scheduledTask.tasks.title,
          startDate: eventStartDate,
          endDate: eventEndDate,
          locality: scheduledTask.tasks.locality,
          localityName: scheduledTask.tasks.locality?.name,
          task: scheduledTask.tasks,
        })
    })
    return tasks
  }, [tasksData, selectedResourceIds])

  const columns: GridColumns = useMemo(
    () => [
      {
        field: 'title',
        headerName: t('title', { ns: 'task' }),
        flex: 2,
        renderCell: params => <DynamicTextTypography text={params.row.title || '-'} variant="body1" fontWeight={500} />,
      },
      {
        field: 'locality',
        headerName: t('locality', { ns: 'common' }),
        flex: 1.5,
        renderCell: params => (
          <DynamicTextTypography text={params.row.locality?.name || '-'} variant="body1" fontWeight={400} />
        ),
      },
      {
        field: 'vesselName',
        headerName: t('resource', { ns: 'task' }),
        flex: 1.5,
        renderCell: params => (
          <DynamicTextTypography text={params.row.vesselName || '-'} variant="body1" fontWeight={400} />
        ),
        hide: selectedResourceIds.length == 0,
      },
      {
        field: 'startDate',
        headerName: t('startDate', { ns: 'common' }),
        flex: 1,
        renderCell: params => (
          <DynamicTextTypography
            text={params.row.startDate ? getFormattedDate(new Date(params.row.startDate)) : '-'}
            variant="body1"
            fontWeight={400}
          />
        ),
      },
      {
        field: 'endDate',
        headerName: t('endDate', { ns: 'common' }),
        flex: 1,
        renderCell: params => (
          <DynamicTextTypography
            text={params.row.endDate ? getFormattedDate(new Date(params.row.endDate)) : '-'}
            variant="body1"
            fontWeight={400}
          />
        ),
      },
    ],
    [t, selectedResourceIds],
  )

  const handleTaskClick = useCallback(
    (eventOrTask: TaskCalendarEvent | IScheduledTask) => {
      // Handle both TaskCalendarEvent (from table) and IScheduledTask (from calendar)
      const scheduledTaskId = eventOrTask.id
      const localityId = 'task' in eventOrTask ? eventOrTask.task?.locality?.id : eventOrTask.tasks?.locality?.id
      if (localityId && scheduledTaskId && scheduledTaskId > 0) {
        navigateTo(AppRoutesNavigation.Localities.workOrder(customerId, localityId, scheduledTaskId))
        pop?.()
      }
    },
    [navigateTo, customerId, pop],
  )

  const handleCalendarEventClick = useCallback(
    (taskEvent: TaskCalendarEvent, event?: any) => {
      const isCtrl = event?.ctrlKey || event?.metaKey
      if (isCtrl) {
        // Check if taskEvent date range conflicts with any selected task's date range
        const hasConflict = selectedTasks.some(
          t => t != taskEvent && doDateRangesOverlap(taskEvent.startDate, taskEvent.endDate, t.startDate, t.endDate),
        )

        if (hasConflict) {
          toast.error(t('a-work-order-with-same-date-range-is-already-selected', { ns: 'task' }))
        } else {
          setSelectedTasks(prev => {
            return prev.includes(taskEvent)
              ? prev.filter(
                  x => x.id !== taskEvent.id && x.startDate !== taskEvent.startDate && x.endDate !== taskEvent.endDate,
                )
              : [...prev, taskEvent]
          })
        }
        return
      } else if (taskEvent && taskEvent.id > 0) {
        const scheduledTask = tasksData?.find((st: IScheduledTask) => st.id === taskEvent.id)
        if (scheduledTask) {
          handleTaskClick(scheduledTask)
        }
      }
    },
    [handleTaskClick, tasksData, selectedTasks, t],
  )

  const handleLocalityCardClick = useCallback(
    (locality: ILocality, event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      const currentLocalityIds = form.getValues('localityIds') || []
      const allLocalities = localities || []

      const newLocalityIds = coreUtils.handleRowSelectFunc(
        currentLocalityIds,
        { id: locality.id },
        allLocalities.map(l => ({ id: l.id })),
        event,
      )

      form.setValue('localityIds', newLocalityIds)
      const formValues = form.getValues()
      taskFilterChange({
        resourceIds: (vesselId ? [{ id: vesselId }] : formValues.resourceIds) || undefined,
        localityIds: newLocalityIds,
      })
    },
    [form, taskFilterChange, vesselId, localities],
  )

  const selectedLocalityIds = form.watch('localityIds') || []
  const selectedLocalityIdSet = useMemo(() => new Set(selectedLocalityIds.map(l => l.id)), [selectedLocalityIds])

  const selectedResourceIdSet = useMemo(() => new Set(selectedResourceIds.map(r => r.id)), [selectedResourceIds])

  return (
    <MainContainer>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflowX: 'hidden' }}>
        {!vesselId && (
          <Box sx={{ flexShrink: 0, marginBottom: 2, width: '100%', maxWidth: '100%' }}>
            <ScrollableLocalityContainer>
              {localities
                ?.sort((a, b) => a.name.localeCompare(b.name))
                .map((locality: ILocality) => {
                  const isSelected = selectedLocalityIdSet.has(locality.id)
                  return (
                    <LocalityCard
                      key={locality.id}
                      onClick={e => handleLocalityCardClick(locality, e)}
                      isSelected={isSelected}
                    >
                      <CardContent>
                        <DynamicTextTypography
                          text={locality.name}
                          variant="body1"
                          fontWeight={isSelected ? 600 : 400}
                          textAlign={'center'}
                          width={'100%'}
                          color={isSelected ? theme.palette.primary.main : theme.palette.text.primary}
                        />
                      </CardContent>
                    </LocalityCard>
                  )
                })}
            </ScrollableLocalityContainer>
          </Box>
        )}
        <Box display={'flex'} flexDirection={'row'} sx={{ minHeight: 0 }}>
          {!vesselId && (
            <VesselSidebar
              isLoading={isResourcesLoading}
              resources={resources}
              selectedResourceIdSet={selectedResourceIdSet}
              form={form}
              taskFilterChange={taskFilterChange}
              vesselId={vesselId}
              customerId={customerId}
              pop={pop}
              selectedTasks={selectedTasks}
              setSelectedTasks={setSelectedTasks}
            />
          )}
          <Box display="flex" flex={1} flexDirection="column">
            <ScrollableContentContainer>
              <Box sx={{ marginBottom: '24px' }}>
                <TaskCalendar
                  onClick={handleCalendarEventClick}
                  paramsStartDate={paramsStartDate}
                  paramsEndDate={paramsEndDate}
                  events={events}
                  selectedTasks={selectedTasks}
                  onMonthChange={dateRangeChange}
                />
              </Box>
              <Box sx={{ marginTop: '24px' }}>
                <GenericTable
                  queryUrl={undefined}
                  queryParams={queryParams}
                  columns={columns}
                  data={events || []}
                  onRowClick={({ row }) => row.id > 0 && handleTaskClick(row)}
                  enableServerPagination={false}
                  id="vessel-tasks-table"
                  autoHeight={false}
                  height="400px"
                  loading={isLoading}
                  getRowId={row => `${row.task.id}-${row.startDate}-${row.endDate}`}
                  getRowClassName={params => {
                    const baseClass = params.indexRelativeToCurrentPage % 2 == 0 ? 'even' : 'odd'
                    const disabledClass = params.row.id === 0 ? 'disabled-row' : ''
                    return `${baseClass} ${disabledClass}`.trim()
                  }}
                  sx={theme => ({
                    '& .disabled-row': {
                      opacity: 0.5,
                      cursor: 'not-allowed',
                      pointerEvents: 'none',
                      backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
                    },
                    '& .disabled-row:hover': {
                      backgroundColor: getThemeColor(theme, MUIThemeColors.secondaryLight),
                    },
                  })}
                />
              </Box>
            </ScrollableContentContainer>
          </Box>
        </Box>
      </Box>
    </MainContainer>
  )
}

// Styled Components
const MainContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flex: 1,
  gap: theme.spacing(2),
  height: '100%',
  overflow: 'inherit',
  width: 'calc(100%)',
  maxWidth: '1536px',
}))

const ScrollableContentContainer = styled(Box)({
  width: 'calc(100% - 40px)',
  height: 'calc(100vh - 310px)',
  maxWidth: '1250px',
  paddingLeft: '10px',
  overflowY: 'scroll',
})
const ScrollableLocalityContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  overflowX: 'auto',
  overflowY: 'hidden',
  paddingBottom: '8px',
  maxHeight: '75px',
  width: 'calc(100% - 60px)',
  maxWidth: '1500px',
  '&::-webkit-scrollbar': {
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    backgroundColor: theme.palette.grey[200],
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: theme.palette.grey[400],
    borderRadius: '4px',
    '&:hover': {
      backgroundColor: theme.palette.grey[500],
    },
  },
}))

interface LocalityCardProps {
  isSelected?: boolean
}

const LocalityCard = styled(Card)<LocalityCardProps>(({ theme, isSelected }) => ({
  cursor: 'pointer',
  minWidth: '200px',
  flexShrink: 0,
  marginTop: '1px',
  border: `2px solid ${isSelected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isSelected ? `${theme.palette.primary.light}20` : theme.palette.background.paper,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  },
}))

export default VesselTasksCalendarDialog
